import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRuler, faCheck, faLayerPlus, faPencil, faLock, faPuzzle, faTemperatureFull, faEye } from "@fortawesome/pro-solid-svg-icons";
import { faBuilding } from "@fortawesome/pro-thin-svg-icons";
import { faSquare } from "@fortawesome/pro-regular-svg-icons";

import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import useDashboardProcess from "@/hooks/use-dashboard-process";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context.ts";

import Card from "@/components/card";
import Button from "@/components/buttons/button";
import TruncatedText from "@/components/text/truncated-text";
import CheckBox from "@/components/checkbox";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import ProjectLockStatus from "@/models/enums/project-lock-status.ts";
import { ValidationMode } from "@/pages/takeoff/layouts/validation/layouts/details/types/validation-mode-type.ts";
import thermalHighlight from "../styles/thermal-highlight.module.css";
import shellHighlight from "../styles/shell-highlight.module.css";
import scaleHighlight from "../styles/scale-highlight.module.css";
import ValidationState from "@/models/enums/validation-state.ts";
import FlowMode from "@/models/enums/flow-mode.ts";

const createMeasureSearchParams = (storeyId: string, mode: ValidationMode): URLSearchParams => new URLSearchParams({ storeyId, mode });

const MeasureCard3D: React.FC = () => {
    const { projectLockStatus } = useProjectContext();
    const { project } = useProjectContext();
    const { dashboardProcessStatusQuery } = useDashboardProcess(project.id);
    const { projectMeasures, storeysFileData } = useProjectMeasureContext();
    const { setRoute } = useProjectFormNavigationContext();
    const { visibleItems, addItem, removeItem } = useViewerItemsVisibilityContext();
    const { validationPacks } = useValidationContext();

    return (
        <div className='flex flex-col gap-y-2 overflow-y-auto pointer-events-auto'>
            {projectMeasures.buildings.map(building => (
                <Card key={building.id} collapsable={true} 
                      additionalHeaderClasses='!pl-2 !pr-4 !py-2' 
                      additionalClasses='max-w-80'
                    title={
                        <div className='flex flex-row items-center gap-x-1 flex-1'>
                            <FontAwesomeIcon icon={faBuilding} className='text-2xl' />
                            <TruncatedText className='font-semibold' text={building.name} />
                        </div>
                    }>

                    <div className='flex flex-col divide-y divide-gray-300'>
                        {building.storeys.map((s) => {
                            const files = storeysFileData.get(s.id) ?? [];
                            const validation = validationPacks?.find((p) => p.storeyId === s.id);
                            return (
                                <div key={`storey-${s.id}`} className='flex flex-col px-2 py-1'>
                                    <div className='flex flex-row items-center gap-x-2'>
                                        <FontAwesomeIcon icon={faSquare} className='rotate-x-60 rotate-z-45' />
                                        <TruncatedText text={s.name} className='flex-1 font-semibold' />
                                        <span className='text-xs text-gray-400'>{s.altimetry} m</span>

                                        {(project.flowMode === FlowMode._3D2D || dashboardProcessStatusQuery.data?.hasCalcul1 === true) &&
                                            <div className='flex flex-row items-center gap-x-1'>
                                                <Button size='xs' variant='simple'
                                                    className={`${thermalHighlight.target} cursor-pointer`}
                                                    aria-label={`Métrés thermiques — ${s.name}`}
                                                    onClick={() => setRoute('validation', createMeasureSearchParams(s.id, 'thermal'))}>
                                                    <FontAwesomeIcon icon={faTemperatureFull} fixedWidth={true} />
                                                </Button>
                                                <Button size='xs' variant='simple'
                                                    className={`${shellHighlight.target} cursor-pointer`}
                                                    aria-label={`Métrés thermiques — ${s.name}`}
                                                    onClick={() => setRoute('validation', createMeasureSearchParams(s.id, 'functional'))}>
                                                    <div className='flex flex-row gap-x-0.5 items-center'>
                                                        {validation?.validation.state !== ValidationState.ToValidate && <FontAwesomeIcon icon={faCheck} fixedWidth={true} className='text-green-500'/>}
                                                        <FontAwesomeIcon icon={faPuzzle} fixedWidth={true} />
                                                    </div>
                                                </Button>
                                            </div>
                                        }
                                    </div>

                                    <div className='ml-6.5 mt-1'>
                                        {files.length > 0 ? (
                                            files.map((f) => {
                                                const hasScale = f.content?.default === false;
                                                const hasFileData = storeysFileData.get(s.id) != null;
                                                return (
                                                    <React.Fragment key={f.name}>
                                                        <div />
                                                        <div className='flex flex-row items-center gap-x-1 min-w-0'>
                                                            <CheckBox
                                                                checked={visibleItems.find(i => i === f.fileId) != null}
                                                                onChange={(e) => {
                                                                    if (e.target.checked)
                                                                        addItem(f.fileId);
                                                                    else
                                                                        removeItem(f.fileId);
                                                                }}
                                                            />

                                                            <TruncatedText truncateLeft={true} text={f.name} className='text-xs min-w-0 flex-1 text-gray-700 mr-3' />

                                                            {projectLockStatus === ProjectLockStatus.LOCK_OWNED ? (
                                                                <>
                                                                    <Button variant='simple' size='xs'
                                                                        className={`${scaleHighlight.target} cursor-pointer`}
                                                                        onClick={() =>
                                                                            setRoute('organisation', new URLSearchParams({ fileId: f.fileId }), 'measures')
                                                                        }>
                                                                        <FontAwesomeIcon icon={faRuler} fixedWidth={true} />
                                                                        {hasScale &&
                                                                            <FontAwesomeIcon icon={faCheck}
                                                                                className={'text-green-500'} />
                                                                        }
                                                                    </Button>
                                                                    <Button variant='simple' size='xs'
                                                                        disabled={!hasScale}
                                                                        className='cursor-pointer'
                                                                        onClick={() =>
                                                                            setRoute('overlay', new URLSearchParams({ fileId: f.fileId }))
                                                                        }>
                                                                        <FontAwesomeIcon icon={faLayerPlus} fixedWidth={true} />
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ProjectEditButton variant='simple' size='xs'
                                                                        disabled={!hasFileData}>
                                                                        <div className='flex flex-row gap-x-2 items-center'>
                                                                            <FontAwesomeIcon icon={faLock}
                                                                                className='text-red-500' />
                                                                            <FontAwesomeIcon icon={faRuler} />
                                                                            <FontAwesomeIcon icon={faLayerPlus} />
                                                                            <FontAwesomeIcon icon={faPencil} />
                                                                        </div>
                                                                    </ProjectEditButton>
                                                                </>
                                                            )}

                                                            <Button size='xs' variant='simple' className='cursor-pointer'
                                                                    disabled={!hasScale || !hasFileData}
                                                                    onClick={() => setRoute('measures', new URLSearchParams({ storeyId: s.id }))}>
                                                                <FontAwesomeIcon icon={projectLockStatus === ProjectLockStatus.LOCK_OWNED ? faPencil : faEye} fixedWidth={true} />
                                                            </Button>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <div className='text-xs text-slate-400'>Aucun fichier</div>
                                        )}
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </Card>
            ))}
        </div>
    );
};

export default MeasureCard3D;